'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import SiteHeader from '@/components/SiteHeader';
import SiteFooter from '@/components/SiteFooter';
import RemovedContent from '@/components/shared/RemovedContent';
import ShareModal from '@/components/shared/ShareModal';
import { Skeleton } from '@/components/shared/Skeletons';
import { mockEvents, getEventBySlug, Event } from '@/data/mockEvents';
import { ArrowLeft, Calendar, MapPin, Users, Clock, Share2, Tag } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


function EventDetailSkeleton() {
  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-10 py-10">
      <Skeleton className="h-4 w-28 mb-8" />
      <Skeleton className="h-48 w-full rounded-xl mb-8" />
      <Skeleton className="h-8 w-3/4 mb-3" />
      <Skeleton className="h-4 w-48 mb-6" />
      <div className="space-y-2 mb-8">
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-5/6" />
      </div>
    </div>
  );
}

function RelatedEventCard({ event }: { event: Event }) {
  return (
    <Link href={`/events/${event.slug}`} className="block group focus-visible:outline-none">
      <div className="neon-card p-4 group-focus-visible:border-[rgba(76,201,255,0.35)]">
        <div className="flex items-start gap-3">
          <div
            className="w-10 h-10 rounded-lg flex flex-col items-center justify-center flex-shrink-0"
            style={{ background: 'rgba(76,201,255,0.08)', border: '1px solid rgba(76,201,255,0.15)' }}
          >
            <span className="text-[8px] font-semibold uppercase tracking-wide" style={{ color: '#4CC9FF' }}>
              {event.date.split(', ')[1]?.split(' ')[0]?.slice(0, 3)}
            </span>
            <span className="font-display font-bold text-[14px] leading-none" style={{ color: '#F2F4FF' }}>
              {event.date.split(', ')[1]?.split(' ')[1]}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-[13px] leading-snug mb-0.5 truncate" style={{ color: '#F2F4FF' }}>
              {event.title}
            </h4>
            <p className="text-[12px]" style={{ color: '#A7A3C4' }}>{event.time} · {event.location.split(',')[0]}</p>
          </div>
        </div>
      </div>
    </Link>
  );
}

export default function EventDetailPage() {
  const params = useParams();
  const slug = params?.eventSlug as string;
  const [isLoading, setIsLoading] = useState(true);
  const [event, setEvent] = useState<Event | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      const found = getEventBySlug(slug);
      if (found) {
        setEvent(found);
      } else {
        setNotFound(true);
      }
      setIsLoading(false);
    }, 600);
    return () => clearTimeout(timer);
  }, [slug]);

  const relatedEvents = mockEvents
    .filter((e) => e.slug !== slug && e.category === event?.category)
    .slice(0, 3);

  return (
    <div className="min-h-screen" style={{ background: '#07060C' }}>
      <SiteHeader theme="dark" />
      <main className="pt-16">
        {isLoading && <EventDetailSkeleton />}

        {!isLoading && notFound && (
          <div className="max-w-3xl mx-auto px-6 lg:px-10 py-10">
            <Link href="/events" className="inline-flex items-center gap-2 text-[13px] mb-8 transition-colors duration-150 hover:opacity-80" style={{ color: '#A7A3C4' }}>
              <ArrowLeft size={14} /> Back to Events
            </Link>
            <RemovedContent variant="event" backHref="/events" backLabel="Back to Events" />
          </div>
        )}

        {!isLoading && event && (
          <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-10">
            <div className="max-w-3xl">
              {/* Back */}
              <Link href="/events" className="inline-flex items-center gap-2 text-[13px] mb-8 transition-colors duration-150 hover:opacity-80" style={{ color: '#A7A3C4' }}>
                <ArrowLeft size={14} /> Back to Events
              </Link>

              {/* Hero visual */}
              <div
                className="w-full h-48 sm:h-64 rounded-xl mb-8 flex items-center justify-center"
                style={{
                  background: event.imageGradient || 'linear-gradient(135deg, rgba(76,201,255,0.12) 0%, rgba(122,92,255,0.10) 100%)',
                  border: '1px solid rgba(255,255,255,0.08)',
                }}
              >
                <div className="text-center">
                  <div
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-3"
                    style={{ background: 'rgba(7,6,12,0.60)', border: '1px solid rgba(255,255,255,0.12)' }}
                  >
                    <div className="w-1.5 h-1.5 rounded-full" style={{ background: '#4CC9FF' }} />
                    <span className="text-[11px] font-medium tracking-wide" style={{ color: '#4CC9FF' }}>Private Community Event</span>
                  </div>
                  <p className="font-display font-bold text-[22px] sm:text-[28px]" style={{ color: '#F2F4FF' }}>{event.title}</p>
                </div>
              </div>

              {/* Title + meta */}
              <div className="flex items-start justify-between gap-4 mb-6">
                <div>
                  <h1 className="font-display font-bold text-[28px] sm:text-[34px] leading-tight mb-2" style={{ color: '#F2F4FF' }}>
                    {event.title}
                  </h1>
                  <span
                    className="inline-flex items-center px-2.5 py-1 rounded-full text-[12px] font-medium"
                    style={{ background: 'rgba(122,92,255,0.10)', border: '1px solid rgba(122,92,255,0.20)', color: '#9B7FFF' }}
                  >
                    {event.category}
                  </span>
                </div>
                <button
                  onClick={() => setShareOpen(true)}
                  className="flex-shrink-0 flex items-center gap-2 px-3 py-2 rounded-lg text-[13px] font-medium transition-all duration-150 hover:bg-white/[0.08] active:scale-95"
                  style={{ color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.10)' }}
                >
                  <Share2 size={14} />
                  Share
                </button>
              </div>

              {/* Event details */}
              <div
                className="rounded-xl p-5 mb-8 grid grid-cols-1 sm:grid-cols-2 gap-4"
                style={{ background: '#0F0D1D', border: '1px solid rgba(255,255,255,0.08)' }}
              >
                {[
                  { icon: Calendar, label: 'Date', value: event.date, color: '#4CC9FF' },
                  { icon: Clock, label: 'Time', value: `${event.time}${event.endTime ? ` – ${event.endTime}` : ''}`, color: '#4CC9FF' },
                  { icon: MapPin, label: 'Location', value: event.location, color: '#4CC9FF' },
                  { icon: Users, label: 'Attendance', value: `${event.attendeeCount} attending${event.maxAttendees ? ` of ${event.maxAttendees}` : ''}`, color: '#4CC9FF' },
                ].map((item) => {
                  const Icon = item.icon;
                  return (
                    <div key={item.label} className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(76,201,255,0.08)' }}>
                        <Icon size={14} style={{ color: item.color }} />
                      </div>
                      <div>
                        <p className="text-[11px] font-medium uppercase tracking-wide mb-0.5" style={{ color: '#A7A3C4' }}>{item.label}</p>
                        <p className="text-[14px] font-medium" style={{ color: '#F2F4FF' }}>{item.value}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Description */}
              <div className="mb-8">
                <h2 className="font-display font-semibold text-[18px] mb-3" style={{ color: '#F2F4FF' }}>About This Event</h2>
                <p className="text-[15px] leading-relaxed" style={{ color: '#A7A3C4' }}>{event.description}</p>
              </div>

              {/* Organizer */}
              <div
                className="rounded-xl p-4 mb-8 flex items-center gap-3"
                style={{ background: 'rgba(76,201,255,0.05)', border: '1px solid rgba(76,201,255,0.12)' }}
              >
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 font-display font-bold text-[12px]"
                  style={{ background: 'linear-gradient(135deg, rgba(76,201,255,0.20), rgba(122,92,255,0.20))', border: '1px solid rgba(76,201,255,0.20)', color: '#4CC9FF' }}
                >
                  {event.organizer.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()}
                </div>
                <div>
                  <p className="text-[11px] font-medium uppercase tracking-wide" style={{ color: '#A7A3C4' }}>Organized by</p>
                  <p className="text-[14px] font-semibold" style={{ color: '#F2F4FF' }}>{event.organizer}</p>
                </div>
              </div>

              {/* Tags */}
              {event.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-10">
                  {event.tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[12px] font-medium"
                      style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)', color: '#A7A3C4' }}
                    >
                      <Tag size={10} />
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              {/* Private notice */}
              <div className="rounded-xl p-4 mb-10" style={{ background: 'rgba(76,201,255,0.04)', border: '1px solid rgba(76,201,255,0.12)' }}>
                <p className="text-[13px] leading-relaxed" style={{ color: 'rgba(167,163,196,0.80)' }}>
                  🔒 This event is private and visible to verified Panorama Towers residents only.
                </p>
              </div>

              {/* Related events */}
              {relatedEvents.length > 0 && (
                <div>
                  <h2 className="font-display font-semibold text-[18px] mb-4" style={{ color: '#F2F4FF' }}>
                    More {event.category} Events
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {relatedEvents.map((e) => <RelatedEventCard key={e.id} event={e} />)}
                  </div>
                </div>
              )}

              {/* Back CTA */}
              <div className="mt-10 pt-8" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                <Link href="/events" className="btn-secondary text-[14px] px-5 py-2.5 inline-flex items-center gap-2">
                  <ArrowLeft size={14} /> Back to All Events
                </Link>
              </div>
            </div>
          </div>
        )}
      </main>
      <SiteFooter />
      <ShareModal isOpen={shareOpen} onClose={() => setShareOpen(false)} contentType="event" title={event?.title} />
    </div>
  );
}
